import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * InfoAccordion - A specialized accordion for displaying info rows with
 * collapsible details. Designed for the workorder detail panel pattern.
 *
 * Key features:
 * - Single item open at a time (collapsible)
 * - Compact summary view with icon, label, and value
 * - Action buttons that don't trigger expand/collapse
 * - Expanded content with background differentiation
 */

interface InfoAccordionProps {
  children: React.ReactNode;
  type?: 'single';
  collapsible?: boolean;
  className?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
}

function InfoAccordion({
  children,
  className,
  type = 'single',
  collapsible = true,
  ...props
}: InfoAccordionProps) {
  return (
    <AccordionPrimitive.Root
      type={type}
      collapsible={collapsible}
      data-slot="info-accordion"
      className={className}
      {...props}
    >
      {children}
    </AccordionPrimitive.Root>
  );
}

interface InfoAccordionItemProps
  extends React.ComponentProps<typeof AccordionPrimitive.Item> {
  isLast?: boolean;
}

function InfoAccordionItem({
  className,
  isLast,
  ...props
}: InfoAccordionItemProps) {
  return (
    <AccordionPrimitive.Item
      data-slot="info-accordion-item"
      className={cn('group', !isLast && 'border-b', className)}
      {...props}
    />
  );
}

interface InfoAccordionRowProps {
  icon: React.ReactNode;
  label: string;
  summary: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * InfoAccordionRow - A row within the accordion that displays:
 * - Icon and label (small caps)
 * - Summary text (truncated)
 * - Optional action buttons (don't trigger expand)
 * - Expandable content area
 */
function InfoAccordionRow({
  icon,
  label,
  summary,
  actions,
  children,
}: InfoAccordionRowProps) {
  return (
    <>
      <div className="flex min-w-0 items-center gap-3 px-4 py-3">
        <AccordionPrimitive.Trigger asChild>
          <button className="flex min-w-0 flex-1 items-center gap-3 text-left hover:opacity-70">
            <ChevronDown
              className="text-muted-foreground h-4 w-4 shrink-0 transition-transform
                group-data-[state=open]:rotate-180"
            />
            <div className="text-muted-foreground shrink-0">{icon}</div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  {label}
                </span>
              </div>
              <div className="mt-0.5 truncate font-medium">{summary}</div>
            </div>
          </button>
        </AccordionPrimitive.Trigger>
        {/* Actions shown inline, outside the collapsible trigger */}
        {actions && (
          <div
            className="flex shrink-0 gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {actions}
          </div>
        )}
      </div>
      <AccordionPrimitive.Content
        className="data-[state=closed]:animate-accordion-up
          data-[state=open]:animate-accordion-down overflow-hidden"
      >
        <div className="bg-muted/10 px-4 py-3 pl-14">{children}</div>
      </AccordionPrimitive.Content>
    </>
  );
}

export { InfoAccordion, InfoAccordionItem, InfoAccordionRow };
